/**
 * Single-WebSocket telemetry subscription manager for TB 4.x.
 *
 * Multiplexes by `cmdId`, reconnects on close with exponential backoff,
 * replays all live subscriptions on reconnect. TB 4.3+ requires a valid JWT
 * in `?token=` — the token provider is consulted on every connect.
 */
import type { EntityRef } from "./types";

export type TsValue = { ts: number; value: number | string | boolean };
export type TbSubState = "connecting" | "open" | "reconnecting" | "closed";

type CmdId = number;
type Subscription = {
  cmdId: CmdId;
  entity: EntityRef;
  keys: readonly string[];
  scope: "LATEST_TELEMETRY" | "TIME_SERIES";
  onData: (vals: Record<string, TsValue>) => void;
  onError?: (err: Error) => void;
};

type WsIncoming = {
  subscriptionId: CmdId;
  errorCode?: number;
  errorMsg?: string | null;
  data?: Record<string, Array<[number, string | number | boolean]>>;
};

export interface TbSubscriptionManagerConfig {
  baseUrl?: string;
  path?: string;
  tokenProvider?: () => string | null | undefined;
  reconnectBaseMs?: number;
  reconnectMaxMs?: number;
}

export class TbSubscriptionManager {
  private ws: WebSocket | null = null;
  private state: TbSubState = "closed";
  private readonly stateListeners = new Set<(s: TbSubState) => void>();
  private readonly subs = new Map<CmdId, Subscription>();
  private nextCmdId = 1;
  private reconnectAttempt = 0;
  private pendingSend: string[] = [];

  constructor(private readonly config: TbSubscriptionManagerConfig = {}) {}

  /** Current socket state — readable so a consumer can seed its UI before
   *  the first transition fires (DATA-02). */
  getState(): TbSubState { return this.state; }

  /**
   * Subscribe to socket-state transitions (DATA-02). Multi-subscriber; the
   * callback fires on EVERY transition — including each lap of the
   * otherwise-silent reconnect loop ("connecting" → "reconnecting" → …) so
   * the store can render "Reconnecting…" instead of a frozen green "Live"
   * pill. Returns an unsubscribe disposer.
   */
  onStateChange(cb: (s: TbSubState) => void): () => void {
    this.stateListeners.add(cb);
    return () => { this.stateListeners.delete(cb); };
  }

  private setState(s: TbSubState): void {
    this.state = s;
    for (const cb of this.stateListeners) {
      try { cb(s); } catch { /* a listener error must not break the socket */ }
    }
  }

  subscribe(
    entity: EntityRef,
    keys: readonly string[],
    onData: (vals: Record<string, TsValue>) => void,
    onError?: (err: Error) => void,
  ): () => void {
    const cmdId = this.nextCmdId++;
    const sub: Subscription = { cmdId, entity, keys, scope: "LATEST_TELEMETRY", onData, onError };
    this.subs.set(cmdId, sub);
    this.ensureConnected();
    this.sendIfOpen({ tsSubCmds: [this.buildCmd(sub)] });
    return () => this.unsubscribe(cmdId);
  }

  close(): void {
    if (this.ws) try { this.ws.close(); } catch { /* ignore */ }
    this.ws = null;
    this.subs.clear();
    this.setState("closed");
  }

  private unsubscribe(cmdId: CmdId) {
    const sub = this.subs.get(cmdId);
    if (!sub) return;
    this.subs.delete(cmdId);
    this.sendIfOpen({ tsSubCmds: [{ ...this.buildCmd(sub), unsubscribe: true }] });
  }

  private buildCmd(sub: Subscription) {
    return {
      entityType: sub.entity.type,
      entityId: sub.entity.id,
      scope: sub.scope,
      cmdId: sub.cmdId,
      keys: sub.keys.join(","),
    };
  }

  private buildUrl(): string {
    const base = this.config.baseUrl ?? "";
    const path = this.config.path ?? "/api/ws/plugins/telemetry";
    let origin: string;
    if (base) origin = base;
    else if (typeof window !== "undefined") origin = window.location.origin;
    else origin = "";
    let url = origin.replace(/^http/, "ws") + path;
    const token = this.config.tokenProvider?.();
    if (token) url += (url.includes("?") ? "&" : "?") + `token=${encodeURIComponent(token)}`;
    return url;
  }

  private ensureConnected() {
    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) return;
    this.connect();
  }

  private connect() {
    this.setState("connecting");
    const url = this.buildUrl();
    let ws: WebSocket;
    try { ws = new WebSocket(url); } catch { this.scheduleReconnect(); return; }
    this.ws = ws;
    ws.onopen = () => {
      this.setState("open");
      this.reconnectAttempt = 0;
      const pending = this.pendingSend; this.pendingSend = [];
      for (const s of pending) ws.send(s);
      for (const sub of this.subs.values()) ws.send(JSON.stringify({ tsSubCmds: [this.buildCmd(sub)] }));
    };
    ws.onmessage = (ev) => this.handleMessage(ev.data);
    ws.onclose = () => {
      this.ws = null;
      if (this.subs.size > 0) this.scheduleReconnect();
      else this.setState("closed");
    };
  }

  private scheduleReconnect() {
    this.setState("reconnecting");
    this.reconnectAttempt++;
    const base = this.config.reconnectBaseMs ?? 1000;
    const max = this.config.reconnectMaxMs ?? 30_000;
    const delay = Math.min(base * 2 ** Math.min(this.reconnectAttempt - 1, 6), max);
    window.setTimeout(() => this.connect(), delay);
  }

  private sendIfOpen(msg: unknown) {
    const payload = JSON.stringify(msg);
    if (this.ws && this.ws.readyState === WebSocket.OPEN) this.ws.send(payload);
    else this.pendingSend.push(payload);
  }

  private handleMessage(raw: unknown) {
    if (typeof raw !== "string") return;
    let msg: WsIncoming;
    try { msg = JSON.parse(raw) as WsIncoming; } catch { return; }
    const sub = this.subs.get(msg.subscriptionId);
    if (!sub) return;
    if (msg.errorCode && msg.errorCode !== 0) {
      sub.onError?.(new Error(`TB WS error ${msg.errorCode}: ${msg.errorMsg ?? "unknown"}`));
      return;
    }
    if (!msg.data) return;
    const out: Record<string, TsValue> = {};
    for (const [key, pairs] of Object.entries(msg.data)) {
      const last = pairs[pairs.length - 1];
      if (!last) continue;
      const [ts, v] = last;
      let value: number | string | boolean;
      if (typeof v === "string") {
        const n = Number(v);
        value = Number.isFinite(n) && v.trim() !== "" ? n : v;
      } else value = v;
      out[key] = { ts, value };
    }
    if (Object.keys(out).length > 0) sub.onData(out);
  }
}
